import styled, { css, keyframes } from 'styled-components';
import Wrapper from '@components/wrapper.styled';
import { StyledBorderedLink } from '@components/header/get-navbar-links.styled';
import Image from '@components/image';

export const headerHeight = '5rem';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const LogoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1rem;
  width: fit-content;
  align-items: center;
  justify-content: center;
`;

export const HeaderWrapper = styled(Wrapper)`
  position: relative;
  z-index: 2;
`;

interface HeaderBgColorProps {
  $backgroundColor: string;
  $visible: boolean;
}

export const HeaderBgColor = styled.div<HeaderBgColorProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: ${({ $backgroundColor }) => `${$backgroundColor}EE`};
  backdrop-filter: blur(1rem);
  z-index: 1;
  box-shadow: 0.25rem 0.5rem 2rem rgb(0 0 0 / 25%);
  transition: opacity 0.25s ease-in-out;
  /* opacity: ${({ $visible }) => ($visible ? 1 : 0)}; */
`;

interface StyledHeaderProps {
  $isActive: boolean;
  $visible: boolean;
}

export const StyledHeader = styled.nav<StyledHeaderProps>`
  ${({ theme, $visible }) => css`
    display: flex;
    padding: 0.5rem 0;
    align-items: center;
    width: 100%;
    min-height: ${headerHeight};
    max-height: max-content;
    position: ${$visible ? 'fixed' : 'absolute'};
    z-index: ${theme.zIndex.navbar};

    ${$visible &&
    css`
      animation: 0.25s ${fadeIn} ease-in;
    `}

    /* Base positioning - always start with top: 0 */
    top: 0;

    /* Apply dynamic ticker offset when header is in fixed/sticky state AND ticker is visible */
    body.ticker-visible &[data-position='fixed'] {
      top: var(--ticker-height, 3rem) !important;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        top: var(--ticker-height-mobile, 2.5rem) !important;
      }
    }

    /* Ensure top: 0 when not sticky, even when ticker is visible */
    body.ticker-visible &[data-position='absolute'] {
      top: 0 !important;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        top: 0 !important;
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      min-height: 5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      min-height: 4rem;
    }
  `}
`;

export const Content = styled.div`
  display: flex;
  gap: 1.5rem;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const Logo = styled(Image)`
  width: 12rem;
  height: 3.25rem;

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 9rem;
    height: 2.5rem;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 8rem;
    height: 2.25rem;
  }
`;

export const Menu = styled.ul`
  display: flex;
  justify-content: flex-end;
  column-gap: 1.5rem;
  align-items: center;
  width: 100%;

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    column-gap: 1.5rem;
  }

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none;
  }
`;

export const MainActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1rem;

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    gap: 0.75rem;
  }
`;

export const MobileContactButton = styled(StyledBorderedLink)`
  ${({ theme }) => css`
    border-color: ${theme.colors.primary};
    border-width: ${theme.borderWidths.ui};
    color: ${theme.colors.secondary};
    display: flex;
    transition: transform 0.125s ease-in-out;

    &:hover {
      transform: scale(1.025);
      border-color: ${theme.colors.secondary};
      color: ${theme.colors.white};
      background-color: ${theme.colors.secondary};
    }

    &:active {
      transform: scale(0.975);
    }

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      display: flex;
    }
  `}
`;

export const MobileExternalContactButton = styled.a`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    height: ${theme.inputSize.md.desktop.height};
    padding: 0 calc(${theme.inputSize.md.desktop.height} / 2);
    border-radius: calc(${theme.inputSize.md.desktop.height} / 2);
    border: ${theme.borderWidths.ui} solid ${theme.colors.primary};
    color: ${theme.colors.primary};
    background-color: transparent;
    font-weight: 500;
    font-family: ${theme.fontFamily.text};
    font-size: 1rem;
    background-image: none;
    text-decoration: none;
    transition: transform 0.125s ease-in-out;

    &:hover {
      transform: scale(1.0625);
    }

    &:active {
      transform: scale(0.9375);
    }

    :hover {
      background-color: ${theme.colors.primary};
      border-color: ${theme.colors.primary};
      color: ${theme.colors.white};
      text-decoration: none;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      height: ${theme.inputSize.sm.mobile.height};
      padding: 0 calc(${theme.inputSize.sm.mobile.height} / 2);
      border-radius: calc(${theme.inputSize.sm.mobile.height} / 2);
      font-size: ${theme.inputSize.sm.mobile.fontSize};
    }
  `}
`;
